import React, { useEffect, useState } from 'react';
import { ToolboxWrapperMandatory, ToolboxWrapperOptional, Wrapper } from './EventToolbox.styles';
import { useRotationStore } from '../../context/RotationContext';
import { useEventTypeStore } from '../../context/EventTypeContext';
import { useEventStore } from '../../context/EventContext';
import eventtypehandler from '../../utils/event/eventtypehandler';
import eventTypeProcessUtils from '../../utils/event/eventTypeProcessUtils';
import { EventTypeProcess } from '../../types/ViewModel/EventTypeProcess';
import ToolboxProcess from './toolboxProcess/ToolboxProcess';
import { EventTypeProcessFactory } from '../../factory/EventTypeProcessFactory';
import { EventTypeProcessInstance } from '../../types/ViewModel/EventTypeProcessInstance';
import { ProcessTreeItem } from '../../types/ViewModel/ProcessTreeItem';
import { EventTypeData } from '../../types/ViewModel/EventTypeData';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import { ReportLocationEnum } from '../../enums/reportLocationEnum';
import EventTypeFactory from '../../factory/EventTypeFactory';
import useRotation from '../../app/useRotation';
import queryParamsUtils from '../../utils/queryParamsUtils';
import { useRetroactiveCargoMode } from '../../context/RetroactiveCargoModeContext';

type Props = {
    twoCol?: boolean;
};

const EventToolboxComponent: React.FC<Props> = () => {
    const evTypePrcFactory = new EventTypeProcessFactory();
    const [mandatoryProcessList, setMandatoryProcessList] = useState<EventTypeProcess[]>([]);
    const [optionalProcessList, setOptionalProcessList] = useState<EventTypeProcess[]>([]);
    const [displayOptionalList, setDisplayOptionalList] = useState<boolean>(true);
    const [processTreeItemList, setProcessTreeItemList] = useState<ProcessTreeItem[]>([]);

    const { eventTypeDataPort, eventTypeDataSea, setSelectedEventType, toolboxQueryInfo, setToolboxQueryInfo } = useEventTypeStore();
    const { fullEventArray } = useEventStore();
    const { selectedNode } = useRotationStore();
    const { getEventTypeProcesses, sortAllProcesses, getNextSeqNr, updateIsSelected } = eventTypeProcessUtils();
    const { filterOnContext } = eventtypehandler();
    const { getRequiredContextObj } = useRotation();
    const { setQueryParamToolbox } = queryParamsUtils();
    const { createNewEventType } = EventTypeFactory();
    const { isRetroactiveCargoModeEnabled } = useRetroactiveCargoMode();

    useEffect(() => {
        if (selectedNode && fullEventArray) {
            setSelectedEventType(null);
            setDisplayOptionalList(true);
            const processOverview = getEventTypeProcesses(
                fullEventArray,
                getEventTypeData(selectedNode),
                selectedNode,
                isRetroactiveCargoModeEnabled
            );
            if (toolboxQueryInfo) {
                updateIsSelected(processOverview.evTypePrcList, toolboxQueryInfo);
                setToolboxQueryInfo(null);
            }
            const mandPrcList = processOverview?.evTypePrcList.filter((prc) => prc.isMandatory);
            sortAllProcesses(mandPrcList);
            setMandatoryProcessList(mandPrcList);
            const optPrcList = processOverview?.evTypePrcList.filter((prc) => !prc.isMandatory);
            sortAllProcesses(optPrcList);
            setOptionalProcessList(optPrcList);
        } else if (!selectedNode) {
            resetToolbox();
        }
    }, [selectedNode, isRetroactiveCargoModeEnabled]);

    useEffect(() => {
        if (selectedNode && fullEventArray && fullEventArray.length > 0) {
            setSelectedEventType(null);
            const processOverview = getEventTypeProcesses(
                fullEventArray,
                getEventTypeData(selectedNode),
                selectedNode,
                isRetroactiveCargoModeEnabled
            );
            const initialMandatoryPrcList = processOverview?.evTypePrcList.filter((prc) => prc.isMandatory);
            let resultMandatoryPrcList = evTypePrcFactory.createEventTypeProcessList(initialMandatoryPrcList);
            const prcTreeList: ProcessTreeItem[] = [];

            let isTopLevelOfTree = true;
            if (processTreeItemList && processTreeItemList.length > 0) {
                processTreeItemList[0].etPrc.sequenceNr = null;
                let ind = 0;
                let keepGoing = true;
                while (keepGoing) {
                    const evTypePrcList = evTypePrcFactory.createEventTypeProcessList(initialMandatoryPrcList);
                    const prc = findPreviousEvTypeProcess(
                        evTypePrcList,
                        processTreeItemList[ind].etPrc,
                        processTreeItemList[ind].prcInst,
                        prcTreeList
                    );
                    if (prc) {
                        resultMandatoryPrcList = evTypePrcList;
                        ind++;
                        if (ind === processTreeItemList.length) {
                            keepGoing = false;
                        }
                        isTopLevelOfTree = false;
                    } else {
                        const prc = findPreviousEvTypeProcessRemovedPrc(
                            evTypePrcList,
                            processTreeItemList[ind].etPrc,
                            processTreeItemList[ind].prcInst,
                            prcTreeList
                        );
                        if (prc) {
                            resultMandatoryPrcList = evTypePrcList;
                            isTopLevelOfTree = false;
                        }
                        keepGoing = false;
                    }
                }
            }
            sortAllProcesses(resultMandatoryPrcList);
            setMandatoryProcessList(resultMandatoryPrcList);
            const optionalPrcList = processOverview?.evTypePrcList.filter((prc) => !prc.isMandatory);
            sortAllProcesses(optionalPrcList);
            setOptionalProcessList(optionalPrcList);

            if (isTopLevelOfTree) {
                setDisplayOptionalList(true);
            }
        }
    }, [fullEventArray, isRetroactiveCargoModeEnabled]);

    const resetToolbox = () => {
        setMandatoryProcessList([]);
        setOptionalProcessList([]);
        setProcessTreeItemList([]);
    };

    const hasInstances = (evTypePrc: EventTypeProcess): boolean => {
        return evTypePrc.evTypePrcInstanceList && evTypePrc.evTypePrcInstanceList.length > 0;
    };

    const hasProcesses = (evTypePrc: EventTypeProcess): boolean => {
        return evTypePrc.evTypePrcList && evTypePrc.evTypePrcList.length > 0;
    };

    const getEventTypeData = (rotNode: IRotationNode): EventTypeData => {
        return rotNode?.reportLocation === ReportLocationEnum.Port ? eventTypeDataPort : eventTypeDataSea;
    };

    const findPreviousEvTypeProcess = (
        evTypePrcList: EventTypeProcess[],
        etPrc: EventTypeProcess,
        prcInst: EventTypeProcessInstance | null,
        prcTreeList: ProcessTreeItem[]
    ): EventTypeProcess | null => {
        if (evTypePrcList && evTypePrcList.length > 0) {
            const eventTypePrc = evTypePrcList.find((evPrc) => evPrc.eventType?.eventTypeGroupId === etPrc.eventType?.eventTypeGroupId) ?? null;
            if (eventTypePrc) {
                // TODO: LOGIC-ERROR - Expanded state management needs implementation
                eventTypePrc.isExpanded = eventTypePrc.isExpandable;
            }
            let evTypePrc: EventTypeProcess | null = null;
            if (etPrc.sequenceNr) {
                evTypePrc =
                    evTypePrcList.find(
                        (evPrc) => evPrc.eventType?.eventTypeGroupId === etPrc.eventType?.eventTypeGroupId && evPrc.sequenceNr === etPrc.sequenceNr
                    ) ?? null;
            } else {
                evTypePrc =
                    evTypePrcList.find(
                        (evPrc) =>
                            evPrc.eventType?.eventTypeGroupId === etPrc.eventType?.eventTypeGroupId && evPrc.isSelectable === etPrc.isSelectable
                    ) ?? null;
            }
            if (evTypePrc) {
                if (prcInst) {
                    if (evTypePrc.evTypePrcInstanceList && evTypePrc.evTypePrcInstanceList.length > 0) {
                        const prcInstItem = evTypePrc.evTypePrcInstanceList.find((instItem) => instItem.sequenceNr === prcInst.sequenceNr);
                        if (prcInstItem) {
                            prcInstItem.isExpanded = true;
                            prcTreeList.unshift(new ProcessTreeItem(evTypePrc, prcInst));
                            return evTypePrc;
                        }
                    }
                } else {
                    prcTreeList.unshift(new ProcessTreeItem(evTypePrc, prcInst));
                    return evTypePrc;
                }
            }
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                if (evTypePrcList[ind].evTypePrcList && evTypePrcList[ind].evTypePrcList.length > 0) {
                    const evTypeProcess = findPreviousEvTypeProcess(evTypePrcList[ind].evTypePrcList, etPrc, prcInst, prcTreeList);
                    if (evTypeProcess) {
                        evTypePrcList[ind].isExpanded = true;
                        // TODO: LOGIC-ERROR - Expanded state management needs implementation
                        evTypeProcess.isExpanded = evTypeProcess.isExpandable && (evTypeProcess.isConcurrent || !etPrc.isSelectable);
                        prcTreeList.unshift(new ProcessTreeItem(evTypePrcList[ind], null));
                        return evTypeProcess;
                    }
                } else if (evTypePrcList[ind].evTypePrcInstanceList && evTypePrcList[ind].evTypePrcInstanceList.length > 0) {
                    let keepGoingInst = true;
                    let indInst = 0;
                    while (keepGoingInst) {
                        if (
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList &&
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList.length > 0
                        ) {
                            const evTypeProcess = findPreviousEvTypeProcess(
                                evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList,
                                etPrc,
                                prcInst,
                                prcTreeList
                            );
                            if (evTypeProcess) {
                                evTypePrcList[ind].evTypePrcInstanceList[indInst].isExpanded = true;
                                evTypePrcList[ind].isExpanded = true;
                                // TODO: LOGIC-ERROR - Expanded state management needs implementation
                                evTypeProcess.isExpanded = evTypeProcess.isExpandable;
                                prcTreeList.unshift(new ProcessTreeItem(evTypePrcList[ind], evTypePrcList[ind].evTypePrcInstanceList[indInst]));
                                return evTypeProcess;
                            }
                        }
                        indInst++;
                        if (indInst === evTypePrcList[ind].evTypePrcInstanceList.length) {
                            keepGoingInst = false;
                        }
                    }
                }
                ind++;
                if (ind === evTypePrcList.length) {
                    keepGoing = false;
                }
            }
        }
        return null;
    };

    const findPreviousEvTypeProcessRemovedPrc = (
        evTypePrcList: EventTypeProcess[],
        etPrc: EventTypeProcess,
        prcInst: EventTypeProcessInstance | null,
        prcTreeList: ProcessTreeItem[]
    ): EventTypeProcess | null => {
        if (evTypePrcList && evTypePrcList.length > 0) {
            const evTypePrc = evTypePrcList.find((evPrc) => evPrc.eventType?.eventTypeGroupId === etPrc.eventType?.eventTypeGroupId) ?? null;
            if (evTypePrc) {
                // TODO: LOGIC-ERROR - Expanded state management needs implementation
                evTypePrc.isExpanded = evTypePrc.isExpandable;
                return evTypePrc;
            }
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                if (evTypePrcList[ind].evTypePrcList && evTypePrcList[ind].evTypePrcList.length > 0) {
                    const evTypeProcess = findPreviousEvTypeProcessRemovedPrc(evTypePrcList[ind].evTypePrcList, etPrc, prcInst, prcTreeList);
                    if (evTypeProcess) {
                        evTypePrcList[ind].isExpanded = true;
                        // TODO: LOGIC-ERROR - Expanded state management needs implementation
                        evTypeProcess.isExpanded = evTypeProcess.isExpandable && (evTypeProcess.isConcurrent || !etPrc.isSelectable);
                        return evTypeProcess;
                    }
                } else if (evTypePrcList[ind].evTypePrcInstanceList && evTypePrcList[ind].evTypePrcInstanceList.length > 0) {
                    let keepGoingInst = true;
                    let indInst = 0;
                    while (keepGoingInst) {
                        if (
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList &&
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList.length > 0
                        ) {
                            const evTypeProcess = findPreviousEvTypeProcessRemovedPrc(
                                evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList,
                                etPrc,
                                prcInst,
                                prcTreeList
                            );
                            if (evTypeProcess) {
                                evTypePrcList[ind].evTypePrcInstanceList[indInst].isExpanded = true;
                                evTypePrcList[ind].isExpanded = true;
                                // TODO: LOGIC-ERROR - Expanded state management needs implementation
                                evTypeProcess.isExpanded = evTypeProcess.isExpandable;
                                return evTypeProcess;
                            }
                        }
                        indInst++;
                        if (indInst === evTypePrcList[ind].evTypePrcInstanceList.length) {
                            keepGoingInst = false;
                        }
                    }
                }
                ind++;
                if (ind === evTypePrcList.length) {
                    keepGoing = false;
                }
            }
        }
        return null;
    };

    const handleProcessClicked = (etPrc: EventTypeProcess, prcInst: EventTypeProcessInstance | null) => {
        setQueryParamToolbox(etPrc.eventType?.eventTypeId, etPrc.sequenceNr);
        setProcessProperties(etPrc, prcInst);
    };

    const resetAllItems = (evTypePrcList: EventTypeProcess[]) => {
        if (evTypePrcList && evTypePrcList.length > 0) {
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                evTypePrcList[ind].isExpanded = false;
                evTypePrcList[ind].isSelected = false;
                if (evTypePrcList[ind].evTypePrcList && evTypePrcList[ind].evTypePrcList.length > 0) {
                    resetAllItems(evTypePrcList[ind].evTypePrcList);
                } else if (evTypePrcList[ind].evTypePrcInstanceList && evTypePrcList[ind].evTypePrcInstanceList.length > 0) {
                    let keepGoingInst = true;
                    let indInst = 0;
                    while (keepGoingInst) {
                        evTypePrcList[ind].evTypePrcInstanceList[indInst].isExpanded = false;
                        evTypePrcList[ind].evTypePrcInstanceList[indInst].isSelected = false;
                        if (
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList &&
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList.length > 0
                        ) {
                            resetAllItems(evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList);
                        }
                        indInst++;
                        if (indInst === evTypePrcList[ind].evTypePrcInstanceList.length) {
                            keepGoingInst = false;
                        }
                    }
                }
                ind++;
                if (ind === evTypePrcList.length) {
                    keepGoing = false;
                }
            }
        }
        return;
    };

    const setDisplayList = (prcMandatoryItem: EventTypeProcess | null, etPrc: EventTypeProcess, prcInst: EventTypeProcessInstance | null) => {
        if (prcMandatoryItem) {
            if (hasInstances(prcMandatoryItem)) {
                if (prcInst) {
                    setDisplayOptionalList(false);
                } else {
                    if (etPrc.isExpanded) {
                        setDisplayOptionalList(true);
                    } else {
                        setDisplayOptionalList(false);
                    }
                }
            } else if (hasProcesses(prcMandatoryItem)) {
                if (etPrc.isExpanded) {
                    setDisplayOptionalList(true);
                } else if (!etPrc.isSelectable) {
                    setDisplayOptionalList(false);
                }
            } else {
                if (etPrc.isExpanded || !etPrc.isExpandable) {
                    setDisplayOptionalList(true);
                } else if (!etPrc.isSelectable) {
                    setDisplayOptionalList(false);
                }
            }
        }
    };

    const setExpanded = (
        evTypePrcList: EventTypeProcess[],
        etPrc: EventTypeProcess,
        prcInst: EventTypeProcessInstance | null,
        prcTreeList: ProcessTreeItem[],
        prcMandatoryItem: EventTypeProcess | null
    ): EventTypeProcess[] => {
        const evTypePrc = findEvTypeProcess(evTypePrcList, etPrc, prcInst, prcTreeList);
        if (evTypePrc) {
            if (prcInst) {
                // A process instance is clicked
                const inst = evTypePrc.evTypePrcInstanceList.find((prc) => prc.sequenceNr === prcInst.sequenceNr);
                if (inst) {
                    evTypePrc.isExpanded = true;
                    inst.isExpanded = !prcInst.isExpanded;
                    inst.isSelected = true;
                }
                sortAllProcesses(evTypePrcList);
            } else {
                if (!prcMandatoryItem) {
                    // if (!hasInstances(evTypePrc) && evTypePrc.isSelectable) {
                    if (!hasInstances(evTypePrc) && !evTypePrc.isExpandable) {
                        evTypePrc.isExpanded = false;
                    } else {
                        evTypePrc.isExpanded = !etPrc.isExpanded;
                    }
                    // } else if (!hasInstances(evTypePrc) && evTypePrc.isSelectable) {
                } else if (!hasInstances(evTypePrc) && !evTypePrc.isExpandable) {
                    evTypePrc.isExpanded = false;
                } else {
                    evTypePrc.isExpanded = !etPrc.isExpanded;
                }
                evTypePrc.isSelected = true;
                sortAllProcesses(evTypePrcList);
            }
        }
        return evTypePrcList;
    };

    const getNextSequenceNr = (etPrc: EventTypeProcess): number | null => {
        const obj = getRequiredContextObj(selectedNode, selectedNode?.type);
        if (obj) {
            const contextSpecificEventList = filterOnContext(obj, fullEventArray);
            return getNextSeqNr(contextSpecificEventList, etPrc.eventType, etPrc.sequenceNr, selectedNode);
            // return getNextSeqNr(contextSpecificEventList, etPrc.eventType, etPrc.eventType!.parentSequenceNumber, selectedNode);
        }
        return null;
        // if (etPrc.isConcurrent) {
        //     if (etPrc.evTypePrcInstanceList && etPrc.evTypePrcInstanceList.length > 0) {
        //         const maxSeqNr = Math.max.apply(Math, etPrc.evTypePrcInstanceList.map(function(o) { return o.sequenceNr!}));
        //         return maxSeqNr + 1;
        //     } else {
        //         return etPrc.sequenceNr;
        //     }
        //     return 1;
        // } else {
        //     return etPrc.sequenceNr!;
        // }
    };

    const updateEventType = (etPrc: EventTypeProcess) => {
        if (etPrc.isSelectable) {
            const newEvType = createNewEventType(etPrc.eventType);
            if (newEvType) {
                newEvType.parentSequenceNumber = getNextSequenceNr(etPrc);
            }
            setSelectedEventType(newEvType);
        } else {
            setSelectedEventType(null);
        }
    };

    const setProcessProperties = (etPrc: EventTypeProcess, prcInst: EventTypeProcessInstance | null) => {
        updateEventType(etPrc);
        const prcTreeList: ProcessTreeItem[] = [];
        if (etPrc.eventType) {
            let evTypeMandPrcList = evTypePrcFactory.createEventTypeProcessList(mandatoryProcessList);
            resetAllItems(evTypeMandPrcList);
            let evTypeOptPrcList = evTypePrcFactory.createEventTypeProcessList(optionalProcessList);
            resetAllItems(evTypeOptPrcList);

            const prcMandatoryItem = evTypeMandPrcList.find((prc) => prc.eventType?.eventTypeId === etPrc.eventType?.eventTypeId) ?? null;
            setDisplayList(prcMandatoryItem, etPrc, prcInst);

            evTypeMandPrcList = setExpanded(evTypeMandPrcList, etPrc, prcInst, prcTreeList, prcMandatoryItem);
            evTypeOptPrcList = setExpanded(evTypeOptPrcList, etPrc, prcInst, prcTreeList, prcMandatoryItem);

            setMandatoryProcessList(evTypeMandPrcList);
            setOptionalProcessList(evTypeOptPrcList);
        }
        setProcessTreeItemList(prcTreeList);
    };

    const findEvTypeProcess = (
        evTypePrcList: EventTypeProcess[],
        etPrc: EventTypeProcess,
        prcInst: EventTypeProcessInstance | null,
        prcTreeList: ProcessTreeItem[]
    ): EventTypeProcess | null => {
        if (evTypePrcList && evTypePrcList.length > 0) {
            let evTypePrc: EventTypeProcess | null = null;
            if (etPrc.sequenceNr) {
                evTypePrc =
                    evTypePrcList.find(
                        (evPrc) =>
                            evPrc.eventType?.eventTypeId === etPrc.eventType?.eventTypeId &&
                            evPrc.sequenceNr === etPrc.sequenceNr &&
                            evPrc.isSelectable === etPrc.isSelectable
                    ) ?? null;
            } else {
                evTypePrc =
                    evTypePrcList.find(
                        (evPrc) => evPrc.eventType?.eventTypeId === etPrc.eventType?.eventTypeId && evPrc.isSelectable === etPrc.isSelectable
                    ) ?? null;
            }
            if (evTypePrc) {
                prcTreeList.unshift(new ProcessTreeItem(evTypePrc, prcInst));
                return evTypePrc;
            }
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                if (evTypePrcList[ind].evTypePrcList && evTypePrcList[ind].evTypePrcList.length > 0) {
                    const evTypeProcess = findEvTypeProcess(evTypePrcList[ind].evTypePrcList, etPrc, prcInst, prcTreeList);
                    if (evTypeProcess) {
                        evTypePrcList[ind].isExpanded = true;
                        evTypeProcess.isExpanded = true;
                        prcTreeList.unshift(new ProcessTreeItem(evTypePrcList[ind], null));
                        return evTypeProcess;
                    }
                } else if (evTypePrcList[ind].evTypePrcInstanceList && evTypePrcList[ind].evTypePrcInstanceList.length > 0) {
                    let keepGoingInst = true;
                    let indInst = 0;
                    while (keepGoingInst) {
                        if (
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList &&
                            evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList.length > 0
                        ) {
                            const evTypeProcess = findEvTypeProcess(
                                evTypePrcList[ind].evTypePrcInstanceList[indInst].evTypePrcList,
                                etPrc,
                                prcInst,
                                prcTreeList
                            );
                            if (evTypeProcess) {
                                evTypePrcList[ind].evTypePrcInstanceList[indInst].isExpanded = true;
                                evTypePrcList[ind].isExpanded = true;
                                evTypeProcess.isExpanded = true;
                                prcTreeList.unshift(new ProcessTreeItem(evTypePrcList[ind], evTypePrcList[ind].evTypePrcInstanceList[indInst]));
                                return evTypeProcess;
                            }
                        }
                        indInst++;
                        if (indInst === evTypePrcList[ind].evTypePrcInstanceList.length) {
                            keepGoingInst = false;
                        }
                    }
                }
                ind++;
                if (ind === evTypePrcList.length) {
                    keepGoing = false;
                }
            }
        }
        return null;
    };

    const getMainKey = (prc: EventTypeProcess) => {
        return 'main-' + prc.eventType?.eventTypeId.toString();
    };

    const newToolbox = () => {
        return (
            <Wrapper>
                {mandatoryProcessList && mandatoryProcessList.length > 0 && (
                    <ToolboxWrapperMandatory>
                        {mandatoryProcessList.map((prc) => {
                            return (
                                <ToolboxProcess
                                    onChange={handleProcessClicked}
                                    evTypeProcess={prc}
                                    key={getMainKey(prc)}
                                    prcKey={'key'}
                                    level={0}
                                ></ToolboxProcess>
                            );
                        })}
                    </ToolboxWrapperMandatory>
                )}
                {displayOptionalList && optionalProcessList && optionalProcessList.length > 0 && (
                    <ToolboxWrapperOptional>
                        {optionalProcessList.map((prc) => {
                            return (
                                <ToolboxProcess
                                    onChange={handleProcessClicked}
                                    evTypeProcess={prc}
                                    key={getMainKey(prc)}
                                    prcKey={'key'}
                                    level={0}
                                ></ToolboxProcess>
                            );
                        })}
                    </ToolboxWrapperOptional>
                )}
            </Wrapper>
        );
    };

    return <>{newToolbox()}</>;
};

export default EventToolboxComponent;
